import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import { selectBenchmarkSelfScoringV1WorkspacesWorkspaceIdBenchmarksBenchmarkIdScoringsScoringIdSelectPost } from '../../generated';
import { toBenchmarkScoring } from '../../queries/list-benchmark-scorings';
import type { BenchmarkScoring } from '../../queries/list-benchmark-scorings';

/*
 * POST /v1/workspaces/{workspace_id}/benchmarks/{benchmark_id}/scorings/{scoring_id}/select: stamps
 * `last_selected_at` on a scored category so the page can reopen on it. Tests answer this path with
 * `tests/mocks/benchmark-handlers.ts`.
 */

export interface SelectBenchmarkScoringMutationVariables {
  workspace_id: string;
  benchmark_id: string;
  scoring_id: string;
}

export type SelectBenchmarkScoringMutation = BenchmarkScoring;

async function selectBenchmarkScoring({
  workspace_id,
  benchmark_id,
  scoring_id,
}: SelectBenchmarkScoringMutationVariables) {
  const { data } =
    await selectBenchmarkSelfScoringV1WorkspacesWorkspaceIdBenchmarksBenchmarkIdScoringsScoringIdSelectPost({
      path: { workspace_id, benchmark_id, scoring_id },
      throwOnError: true,
    });
  return toBenchmarkScoring(data);
}

export const useSelectBenchmarkScoringMutation = <TError = unknown, TContext = unknown>(
  options?: Omit<
    UseMutationOptions<SelectBenchmarkScoringMutation, TError, SelectBenchmarkScoringMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  return useMutation<SelectBenchmarkScoringMutation, TError, SelectBenchmarkScoringMutationVariables, TContext>({
    mutationKey: ['SelectBenchmarkScoring'],
    mutationFn: selectBenchmarkScoring,
    ...options,
  });
};

useSelectBenchmarkScoringMutation.fetcher = (variables: SelectBenchmarkScoringMutationVariables) => () =>
  selectBenchmarkScoring(variables);
